import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroBriefcase,
  heroCheckBadge,
  heroEnvelope,
  heroGlobeAlt,
  heroInformationCircle,
  heroMapPin,
} from '@ng-icons/heroicons/outline';
import { finalize } from 'rxjs';
import { JobOfferService } from 'src/app/api/services';
import { CompanyService } from 'src/app/api/services/company.service';
import { APP_CONSTANTS } from 'src/app/core/constant/constants';
import { MESSAGES } from 'src/app/core/constant/messages';
import { LoadingComponent } from 'src/app/shared/component/loading/loading.component';
import { CompanyAdmin } from 'src/app/shared/model/admin';
import { JobOfferAvancedResult } from 'src/app/shared/model/job-offer-result';
import { ToastService } from 'src/app/shared/service/toast.service';
import { handleApiError } from 'src/app/shared/util/handle-api-error';

@Component({
  selector: 'app-company-detail',
  imports: [CommonModule, LoadingComponent, NgIcon, LoadingComponent],
  templateUrl: './company-detail.component.html',
  viewProviders: provideIcons({
    heroMapPin,
    heroCheckBadge,
    heroInformationCircle,
    heroEnvelope,
    heroBriefcase,
    heroGlobeAlt,
  }),
})
export class CompanyDetailComponent implements OnInit {
  company: CompanyAdmin | null = null;
  isLoading: boolean = false;
  isLoadingJobs: boolean = false;
  jobs: JobOfferAvancedResult[] = [];
  selectedTab: 'about' | 'jobs' = 'about';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private companyService: CompanyService,
    private jobOfferService: JobOfferService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.loadCompany();
  }

  loadJobs(): void {
    this.isLoadingJobs = true;
    const id: string | null = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.jobOfferService
        .getByIdCompany(id)
        .pipe(
          handleApiError(this.toastService, MESSAGES.JOB_OFFER.GET_ALL_ERROR)
        )
        .subscribe({
          next: (response: any) => {
            this.jobs = response.dto.jobOffers.data;
            this.isLoadingJobs = false;
          },
          error: () => {
            this.router.navigate(['/']);
            this.isLoadingJobs = false;
          },
        });
    }
  }

  private loadCompany(): void {
    const id: string | null = this.route.snapshot.paramMap.get('id');
    this.isLoading = true;

    if (id) {
      this.companyService
        .getCompanyById(id)
        .pipe(
          handleApiError(this.toastService, MESSAGES.COMPANY.GET_COMPANY_ERROR),
          finalize(() => (this.isLoading = false))
        )
        .subscribe({
          next: (response: any) => {
            this.company = response.dto.company;
          },
          error: () => {
            this.router.navigate(['/user/company']);
          },
        });
    } else {
      this.toastService.showMessages('error', [MESSAGES.GENERAL.ID_INVALID]);
      this.router.navigate(['/user/company']);
      this.isLoading = false;
    }
  }

  isVerified(company: CompanyAdmin | null): boolean {
    return company?.verified === '1';
  }

  get companyImage(): string {
    const url = this.company?.urlBannerMiniPage;
    if (!url || url.trim() === '') {
      return APP_CONSTANTS.DEFAULT_IMAGE_COMPANY;
    }
    return url;
  }

  get companyBanner(): string {
    const url = this.company?.urlBannerMvv;
    if (!url || url.trim() === '') {
      return APP_CONSTANTS.DEFAULT_BANNER_COMPANY;
    }
    return url;
  }

  get companyGroup(): string {
    const url = this.company?.urlBannerOurHistory;
    if (!url || url.trim() === '') {
      return APP_CONSTANTS.DEFAULT_TEAM_COMPANY;
    }
    return url;
  }

  displayOffer(idJobOffer: string): void {
    this.router.navigate(['/job', idJobOffer]);
  }

  displaySalary(job: JobOfferAvancedResult): string {
    if (job.showSalary === '1') {
      const min = Number(job.minimumSalary);
      const max = Number(job.maximumSalary);

      if (!isNaN(min) && !isNaN(max)) {
        const average = ((min + max) / 2).toFixed(2);
        return `s/. ${average}`;
      } else if (!isNaN(min)) {
        return `s/. ${min.toFixed(2)}`;
      } else if (!isNaN(max)) {
        return `s/. ${max.toFixed(2)}`;
      }
    }
    return 'No especificado';
  }
}
